import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, type IconKey } from "../icons";
import { LanguageSwitcher } from "../i18n/LanguageSwitcher";
import {
  getActingFirmId,
  getSystemHealth,
  listFirms,
  parseTokenClaims,
  setActingFirmId,
  setToken,
  type FirmProfile,
  type SystemHealth,
} from "../api";

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconKey;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  divider?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/firms", labelKey: "nav.firms", icon: "blocks", superAdminOnly: true },
  { to: "/overview", labelKey: "nav.overview", icon: "chart" },
  { to: "/map", labelKey: "nav.map", icon: "map" },
  { to: "/routes", labelKey: "nav.routes", icon: "pin" },
  { to: "/vehicles", labelKey: "nav.vehicles", icon: "truck" },
  { to: "/events", labelKey: "nav.events", icon: "bolt" },
  { to: "/driver", labelKey: "nav.driver", icon: "eye" },
  { to: "/delivery", labelKey: "nav.delivery", icon: "boxes" },
  { to: "/fuel", labelKey: "nav.fuel", icon: "fuel" },
  { to: "/expenses", labelKey: "nav.expenses", icon: "receipt" },
  { to: "/ocr", labelKey: "nav.ocr", icon: "scan" },
  { to: "/border", labelKey: "nav.border", icon: "thermometer" },
  { to: "/reports", labelKey: "nav.reports", icon: "chart", divider: true },
  { to: "/alerts", labelKey: "nav.alerts", icon: "bell" },
  { to: "/fleets", labelKey: "nav.fleets", icon: "blocks" },
  { to: "/devices", labelKey: "nav.devices", icon: "cpu" },
  { to: "/users", labelKey: "nav.users", icon: "users", adminOnly: true },
  { to: "/targets", labelKey: "nav.targets", icon: "headset" },
  { to: "/settings", labelKey: "nav.settings", icon: "settings" },
];

function useNow(intervalMs = 30_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

// Tarih/saat aktif dile göre Intl ile biçimlenir (locale-duyarlı, sabit
// Türkçe ay/gün dizileri kaldırıldı).
function formatClock(now: Date, locale: string): { date: string; time: string } {
  const date = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
    weekday: "long",
  }).format(now);
  const time = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(now);
  return { date, time };
}

function SystemHealthDropdown({
  health,
  error,
}: {
  health: SystemHealth | null;
  error: string | null;
}) {
  const { t, i18n } = useTranslation("common");
  return (
    <div className="ds-system-dropdown">
      <div className="ds-system-dropdown-head">
        <strong>{t("systemStatus.backendServices")}</strong>
        {health?.checked_at && (
          <span className="muted small">
            {new Date(health.checked_at).toLocaleTimeString(
              i18n.resolvedLanguage,
            )}
          </span>
        )}
      </div>
      {error && (
        <p className="error small">{t("systemStatus.gatewayDown", { error })}</p>
      )}
      {!health && !error && (
        <p className="muted small">{t("systemStatus.loading")}</p>
      )}
      {health && (
        <ul className="ds-system-list">
          {health.services.map((s) => (
            <li key={s.service} className={`ds-system-row ds-system-row-${s.status}`}>
              <span className={`ds-system-row-dot ds-system-row-dot-${s.status}`} />
              <span className="ds-system-row-name">{s.service}</span>
              <span className="ds-system-row-meta">
                {s.status === "up" && s.latency_ms != null
                  ? `${s.latency_ms} ms`
                  : s.status === "unconfigured"
                    ? t("systemStatus.unconfigured")
                    : s.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function useBackendHealth(): {
  health: SystemHealth | null;
  error: string | null;
} {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await getSystemHealth();
        if (!cancelled) {
          setHealth(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    };
    void tick();
    const t = setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);
  return { health, error };
}

function useUnreadAlerts(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const token = localStorage.getItem("aice.access_token");
        if (!token) return;
        const res = await fetch("/api/v1/alerts", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const raw = await res.json();
        const list = Array.isArray(raw) ? raw : [];
        const openCount = list.filter(
          (a: { status?: string }) =>
            typeof a?.status === "string" && a.status.toLowerCase() === "open",
        ).length;
        if (!cancelled) setCount(openCount);
      } catch {
        // Silent — alert bell is a nice-to-have, not load-bearing.
      }
    };
    void tick();
    const t = setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);
  return count;
}

// Company switcher (SUPERADMIN only). Picking a firm makes the whole panel act
// as that tenant (the gateway scopes every call to it via X-AICE-Acting-Firm);
// "All firms" clears the selection for the platform-wide aggregate view. The
// active firm lives in localStorage, so a full reload re-fetches every page
// already scoped — simpler and less error-prone than threading it through state.
function CompanySwitcher() {
  const { t } = useTranslation("common");
  const [firms, setFirms] = useState<FirmProfile[]>([]);
  const acting = getActingFirmId() ?? "";

  useEffect(() => {
    let cancelled = false;
    listFirms()
      .then((f) => {
        if (!cancelled) setFirms(f);
      })
      .catch(() => {
        // Non-fatal: if the roster can't load the switcher just stays empty.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function onChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setActingFirmId(e.target.value || null);
    // Re-fetch everything under the new tenant scope.
    window.location.reload();
  }

  return (
    <select
      className="ds-firm-switcher"
      value={acting}
      onChange={onChange}
      aria-label={t("shell.companySwitcher")}
      title={t("shell.companySwitcher")}
    >
      <option value="">{t("shell.allFirms")}</option>
      {firms.map((f) => (
        <option key={f.id} value={f.id}>
          {f.name}
        </option>
      ))}
    </select>
  );
}

export function DashboardShell({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation("common");
  const claims = parseTokenClaims();
  const location = useLocation();
  const now = useNow();
  const fmt = formatClock(now, i18n.resolvedLanguage ?? "tr");
  const { health, error: healthError } = useBackendHealth();
  const unreadAlerts = useUnreadAlerts();
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 1100);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef<HTMLDivElement | null>(null);

  // Close the status dropdown when clicking outside it.
  useEffect(() => {
    if (!statusOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!statusRef.current?.contains(e.target as Node)) {
        setStatusOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [statusOpen]);

  // Map backend overall to legacy ÇEVRİMİÇİ/BOZULMUŞ/ÇEVRİMDIŞI labels.
  const overall: "online" | "degraded" | "offline" = healthError
    ? "offline"
    : health?.status === "up"
      ? "online"
      : health?.status === "degraded"
        ? "degraded"
        : "online";  // unknown -> render online (no data yet)

  const activeItem = NAV_ITEMS.find((i) => location.pathname.startsWith(i.to));
  const activeLabel = activeItem ? t(activeItem.labelKey) : "AICE";

  const onLogout = () => {
    setToken(null);
    window.location.href = "/";
  };

  return (
    <div className={`ds-shell${collapsed ? " ds-collapsed" : ""}${mobileOpen ? " ds-mobile-open" : ""}`}>
      <aside className="ds-sidebar">
        <div className="ds-sidebar-head">
          <Link to="/" className="ds-brand" aria-label="AICE">
            <img src="/logo-mark.png" alt="AICE" />
            {!collapsed && <span className="ds-brand-text">AICE</span>}
          </Link>
          <button
            type="button"
            className="ds-collapse-btn"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={t("shell.collapseMenu")}
          >
            <Icon name="menu" size={18} />
          </button>
        </div>
        <nav className="ds-nav">
          {NAV_ITEMS.map((item) => {
            const role = claims?.role;
            if (item.superAdminOnly && role !== "SUPERADMIN") return null;
            // SUPERADMIN keeps admin privileges for admin-only items too.
            if (item.adminOnly && role !== "ADMIN" && role !== "SUPERADMIN") return null;
            return (
              <div key={item.to}>
                {item.divider && <div className="ds-nav-divider" />}
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `ds-nav-link${isActive ? " active" : ""}`
                  }
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon name={item.icon} size={18} />
                  {!collapsed && <span>{t(item.labelKey)}</span>}
                </NavLink>
              </div>
            );
          })}
        </nav>
        <div className="ds-sidebar-foot">
          <button type="button" className="ds-logout" onClick={onLogout}>
            <Icon name="logout" size={18} />
            {!collapsed && <span>{t("shell.logout")}</span>}
          </button>
        </div>
      </aside>

      <div className="ds-main">
        <header className="ds-topbar">
          <button
            type="button"
            className="ds-mobile-toggle"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={t("shell.openMenu")}
          >
            <Icon name="menu" />
          </button>
          <div className="ds-topbar-title">
            <span className="ds-topbar-page">{activeLabel}</span>
            <span className="ds-topbar-firm">
              {claims?.role === "SUPERADMIN"
                ? t("shell.firm", { id: getActingFirmId() ?? t("shell.allFirms") })
                : claims?.firm_id
                  ? t("shell.firm", { id: claims.firm_id })
                  : ""}
            </span>
          </div>
          <div className="ds-topbar-right">
            {claims?.role === "SUPERADMIN" && <CompanySwitcher />}
            <div className="ds-system-wrap" ref={statusRef}>
              <button
                type="button"
                className={`ds-system-status ds-system-${overall}`}
                onClick={() => setStatusOpen((o) => !o)}
                aria-expanded={statusOpen}
                aria-label={t("systemStatus.detailAria")}
              >
                <span className="ds-system-dot" />
                <span>
                  {t("systemStatus.label")}: {t(`systemStatus.${overall}`)}
                </span>
                <Icon name="chart" size={14} />
              </button>
              {statusOpen && (
                <SystemHealthDropdown health={health} error={healthError} />
              )}
            </div>
            <NavLink to="/alerts" className="ds-bell" aria-label={t("shell.alertsAria")}>
              <Icon name="bell" />
              {unreadAlerts > 0 && (
                <span className="ds-bell-badge">{unreadAlerts}</span>
              )}
            </NavLink>
            <LanguageSwitcher className="ds-lang" />
            <div className="ds-clock">
              <div className="ds-clock-time">{fmt.time}</div>
              <div className="ds-clock-date">{fmt.date}</div>
            </div>
            <NavLink to="/settings" className="ds-profile" aria-label={t("shell.profileAria")}>
              <div className="ds-avatar">
                {(claims?.user_id ?? "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="ds-profile-meta">
                <div className="ds-profile-name">{claims?.role ?? "?"}</div>
                <div className="ds-profile-sub">{t("shell.fleetManager")}</div>
              </div>
            </NavLink>
          </div>
        </header>
        <main className="ds-content">{children}</main>
      </div>
    </div>
  );
}
